import type { ComponentProps } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import Chip from '@/components/UI/Chip';
import Row from '@/components/UI/Row';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';

export type ChipPreferenceOption<Value extends string> = {
  icon: ComponentProps<typeof Icon>['name'];
  label: string;
  labelSuffix?: string;
  value: Value;
};

type ChipPreferenceRowProps<Value extends string> = {
  disabled?: boolean;
  label: string;
  onValueChange: (value: Value) => void;
  options: readonly ChipPreferenceOption<Value>[];
  testIDPrefix?: string;
  value: Value;
};

export default function ChipPreferenceRow<Value extends string>({
  disabled = false,
  label,
  onValueChange,
  options,
  testIDPrefix,
  value,
}: ChipPreferenceRowProps<Value>) {
  return (
    <Row spaced style={styles.row}>
      <Text flexShrink={1}>{label}</Text>
      <Box
        accessibilityLabel={label}
        accessibilityRole="radiogroup"
        direction="horizontal"
        space="sm"
        flexWrap="wrap"
        style={styles.options}
      >
        {options.map((option) => (
          <Chip
            accessibilityRole="radio"
            disabled={disabled}
            key={option.value}
            label={option.label}
            labelSuffix={option.labelSuffix}
            onValueChange={() => onValueChange(option.value)}
            size="md"
            testID={
              testIDPrefix ? `${testIDPrefix}-${option.value}` : undefined
            }
            value={value === option.value}
          >
            <Icon
              name={option.icon}
              size="md"
              variant={
                value === option.value ? 'typographyInverted' : 'typography'
              }
            />
          </Chip>
        ))}
      </Box>
    </Row>
  );
}

const styles = StyleSheet.create({
  options: {
    width: {
      xs: '100%',
      sm: 'auto',
    },
  },
  row: {
    alignItems: {
      xs: 'stretch',
      sm: 'center',
    },
    flexDirection: {
      xs: 'column',
      sm: 'row',
    },
  },
});
